import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { Link, useLocation, useNavigate, useRouter } from "@tanstack/react-router"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { type MouseEvent, useState } from "react"
import { toast } from "sonner"

import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring } from "@/components/Flex.ts"
import { PaginationDot, PaginationDots } from "@/components/PaginationDots/PaginationDots.tsx"
import { PaginationModel } from "@/components/PaginationDots/PaginationModel.ts"
import { ProjectSelectorModel } from "@/components/ProjectSelector/ProjectSelectorModel.ts"
import { ProjectSettingsDialog } from "@/components/ProjectSettingsDialog/ProjectSettingsDialog.tsx"
import { TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useNewProjectLocation, useNewTaskLocation, useProjectLocation } from "@/hooks/useMatterLocation.ts"
import { useMediaQuery } from "@/hooks/useMediaQuery.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useInvalidateQueries, useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { CreateSemibold, Icon16, Icon20, MoreRegular } from "@/icons/Icons.ts"
import { batchInfo } from "@/lib/apiClient/client.ts"
import { formatErrorMessage } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { projectsV2QueryKey } from "@/lib/queries.ts"
import { useFavoriteProjects, useProjects } from "@/routes/home/homeHooks.ts"
import { initTaskLocation, Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId, useProjectPermissions } from "@/routes/projects/projectHooks.ts"
import { ellipsis, mobileBreakpoint } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ProjectItemModel } from "@/components/ProjectSelector/ProjectSelectorModel.ts"
import type { PropsWithChildren } from "react"

export const ProjectSelector = observer(() => {
  const context = useOrgContext()

  const initialProjects = useProjects()
  const favoriteProjects = useFavoriteProjects()

  const model = useModel(() => {
    return new ProjectSelectorModel(context, initialProjects, favoriteProjects)
  }, [initialProjects, favoriteProjects])

  const paginationModel = useModel(() => new PaginationModel(model.projects), [model])

  const isMobile = useMediaQuery(mobileBreakpoint)
  const selected = model.selected.get()

  return (
    <>
      <Root
        ref={node => {
          model.refCallback(node)
          paginationModel.refCallback(node)
        }}
        hasLeftShadow={paginationModel.hasLeftShadow.get()}
        hasRightShadow={paginationModel.hasRightShadow.get()}
      >
        <ScrollContainer data-scroll-container>
          <List>
            <NewProjectCard />

            {model.projects.get().map(project => (
              <Item key={project.id} data={project} onSettingsClick={model.showSettings} />
            ))}

            {model.loadingNext.get() && <Spinner />}
          </List>
        </ScrollContainer>
      </Root>

      {!isMobile && paginationModel.totalDots.get() > 1 && (
        <PaginationDots>
          {Array.from({ length: paginationModel.totalDots.get() }, (_, i) => (
            <PaginationDot
              key={i}
              active={i === paginationModel.currentDot.get()}
              onClick={() => paginationModel.navigateToDot(i)}
            />
          ))}
        </PaginationDots>
      )}

      {selected && (
        <ProjectSettingsDialog
          project={selected.data}
          dialogOpen={model.settingsDialogOpen.get()}
          setDialogOpen={model.setSettingsDialogOpen}
        />
      )}
    </>
  )
})

const Item = observer((props: {
  data: ProjectItemModel
  onSettingsClick: (project: ProjectItemModel) => void
}) => {
  const project = props.data
  const isDropdownOpen = project.isDropdownOpen.get()

  const router = useRouter()
  const navigate = useNavigate()
  const orgId = useCurrentOrgId()

  const queries = useQueries()
  const invalidateQueries = useInvalidateQueries()

  const currentLocation = useLocation()

  const projectLocation = useProjectLocation(project.key)
  const newTaskLocation = useNewTaskLocation(project.key)

  const initLocation = props.data.initProjectTaskId
    ? initTaskLocation(orgId, project.key, props.data.initProjectTaskId)
    : null

  // Figure out the final location for the card, avoiding redirects
  const cardLocation = initLocation || (props.data.isEmpty ? newTaskLocation : projectLocation)

  const handleCreateTask = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    startViewTransition(() => navigate(Navigator.newTask(orgId, project.key, {
      closeLocation: { href: currentLocation.href }
    })))
  }

  const handleDeleteProject = async () => {
    setDeleteInProgress(true)

    try {
      await queries.apiClient.deleteProject(props.data.key)
    } catch (e) {
      setDeleteInProgress(false)
      toast.error(formatErrorMessage(e, "Failed to delete project"))
    }

    void invalidateQueries({ queryKey: projectsV2QueryKey })
    await queries.listProjectsV2(batchInfo(ProjectSelectorModel.getBatchSize()))
    await router.invalidate()

    setDeleteInProgress(false)
    setConfirmDeleteDialogOpen(false)
  }

  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)
  const [deleteInProgress, setDeleteInProgress] = useState(false)

  return (
    <Card {...cardLocation} data-testid={project.title} viewTransition>
      <Horizontal alignItems="center" gap="8px">
        <EllipsisContainer>
          <Title>
            {project.title}
          </Title>
        </EllipsisContainer>

        <Spring />

        <DropdownMenu open={isDropdownOpen} onOpenChange={project.setDropdownOpen}>
          <DropdownMenuTrigger tabIndex={-1}>
            <Tooltip content="More">
              <MoreButton
                asChild
                tabIndex={0}
                kind="icon"
                onClick={project.onDropdownClick}
                visible={isDropdownOpen.toString()}
                data-testid="project-more-button"
              >
                <MoreRegular className={Icon20} />
              </MoreButton>
            </Tooltip>
          </DropdownMenuTrigger>

          {isDropdownOpen && (
            <MoreMenuContent
              project={props.data}
              onSettingsClick={props.onSettingsClick}
              setConfirmDeleteDialogOpen={setConfirmDeleteDialogOpen}
            />
          )}
        </DropdownMenu>

        <ConfirmDialog
          open={confirmDeleteDialogOpen}
          onOpenChange={setConfirmDeleteDialogOpen}
          trigger={null}
          description="Are you sure you want to delete this project? This action cannot be undone."
          confirmText={deleteInProgress ? ellipsize("Deleting") : "Delete project"}
          disabled={deleteInProgress}
          onConfirm={handleDeleteProject}
        />

        <Tooltip content="New idea">
          <CreateTaskButton
            kind="icon"
            onClick={handleCreateTask}
            visible={isDropdownOpen.toString()}
            data-testid="new-idea-plus-button"
          >
            <CreateSemibold className={Icon16} />
          </CreateTaskButton>
        </Tooltip>
      </Horizontal>

      <Subtitle data-testid="ideas-count">
        {project.subtitle}
      </Subtitle>

      <Spring />

      <Preview>
        <TaskList>
          {project.data.latestTasks.slice(0, 3).map(task => (
            <TaskItem key={task.id}>
              {task.title}
            </TaskItem>
          ))}
        </TaskList>

        <Cover url={project.coverUrl} color={project.color} />
      </Preview>

      <Underlay color={project.color} />
    </Card>
  )
})

const MoreMenuContent = observer(({ project, onSettingsClick, setConfirmDeleteDialogOpen }: {
  project: ProjectItemModel
  onSettingsClick: (project: ProjectItemModel) => void
  setConfirmDeleteDialogOpen: (open: boolean) => void
}) => {
  const orgId = useCurrentOrgId()
  const projectPermissions = useProjectPermissions(project.key)

  const handleAddToFavorites = (e: MouseEvent) => {
    e.preventDefault()
    void project.toggleFavorite(true)
    runInAction(() => project.isDropdownOpen.set(false))
  }

  const handleRemoveFromFavorites = (e: MouseEvent) => {
    e.preventDefault()
    void project.toggleFavorite(false)
    runInAction(() => project.isDropdownOpen.set(false))
  }

  const handleSettings = (e: MouseEvent) => {
    e.preventDefault()
    onSettingsClick(project)
  }

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation()
    if (projectPermissions.canEdit) {
      setConfirmDeleteDialogOpen(true)
    }
  }

  return (
    <DropdownMenuContent align="end">
      {project.favorite.get() ? (
        <StyledDropdownMenuItem onClick={handleRemoveFromFavorites}>
          Remove from favorites
        </StyledDropdownMenuItem>
      ) : (
        <StyledDropdownMenuItem onClick={handleAddToFavorites}>
          Add to favorites
        </StyledDropdownMenuItem>
      )}

      <StyledDropdownMenuItem onClick={handleSettings}>
        <SettingsLink
          {...Navigator.projectSettings(orgId, project.key, TabId.General)}
          onClick={(e) => e.preventDefault()}
        >
          Settings
        </SettingsLink>
      </StyledDropdownMenuItem>

      <StyledDropdownMenuItem onClick={handleDelete} disabled={!projectPermissions.canEdit}>
        Delete project
      </StyledDropdownMenuItem>
    </DropdownMenuContent>
  )
})

export const Cover = (props: PropsWithChildren<{
  url?: string | null
  color?: string
}>) => {
  const [loadingError, setLoadingError] = useState(false)

  return (
    <CoverRoot>
      <CoverWrap color={props.color}>
        {props.url && (
          <img
            src={props.url}
            alt="Project cover"
            onError={() => setLoadingError(true)}
            className={cx(loadingError ? InvisibleImage : null)}
          />
        )}

        {props.children}
      </CoverWrap>
    </CoverRoot>
  )
}

const NewProjectCard = () => {
  const location = useNewProjectLocation()

  return (
    <CardNew data-testid="add-new-project" {...location} viewTransition>
      <EllipsisContainer>
        <Title>
          Add project
        </Title>
      </EllipsisContainer>

      <Subtitle>
        to implement your ideas
      </Subtitle>

      <Spring />

      <Preview>
        <TaskList>
          <TaskItem>Onboarding flow</TaskItem>
          <TaskItem>Animation for the landing page</TaskItem>
          <TaskItem>Payment screen</TaskItem>
        </TaskList>

        <Cover>
          <Gradient />
        </Cover>
      </Preview>

      <Underlay />
    </CardNew>
  )
}

const Root = styled.div<{ hasLeftShadow: boolean, hasRightShadow: boolean }>`
  display: grid;
  align-items: center;

  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  height: 100%;
  overflow: auto;

  mask-image: linear-gradient(
    to right,
    ${props => props.hasLeftShadow ? "transparent" : "black"},
    black 88px,
    black calc(100% - 88px),
    ${props => props.hasRightShadow ? "transparent" : "black"}
  );

  view-transition-name: project-selector;
`

const ScrollContainer = styled.div`
  width: max-content;
  min-width: 100%;
  padding: 8px 88px;
`

const List = styled(Horizontal)`
  align-items: center;
  justify-content: center;
  gap: 32px;
`

const Card = styled(Link)`
  position: relative;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  gap: 4px;

  max-width: 320px;
  min-height: 360px;
  padding: 30px 24px;

  border-radius: var(--border-radius-12);
  background-color: var(--background-color);
  color: var(--text-color) !important;
`

const CardNew = styled(Card)`
  outline: 1px dashed var(--main-color-50);
  background-color: transparent;

  &:hover {
    outline-style: solid;

    background-color: var(--main-color-5);
  }
`

const CreateTaskButton = styled(Button)<{ visible: string }>`
  display: ${props => props.visible === "true" ? "flex" : "none"};

  ${Card}:hover & {
    display: flex;
  }
`

const MoreButton = styled(Button)<{ visible: string }>`
  display: ${props => props.visible === "true" ? "flex" : "none"};

  ${Card}:hover & {
    display: flex;
  }
`

const Title = styled.h2`
  ${ellipsis};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;

  margin: 0 6px;
`

const Subtitle = styled.h3`
  ${ellipsis};
  ${textStyles.monospaceFont};
  ${textStyles.smallerFontSize};
  font-weight: normal;

  margin: 0 6px;

  opacity: 0.5;
`

const Preview = styled.div`
  position: relative;
`

const CoverRoot = styled(Horizontal)`
  position: relative;
  overflow: hidden;
  z-index: 1;

  padding: 6px;

  background: var(--background-color);
  border-radius: var(--border-radius-12);

  --preview-shadow: 15%;
  box-shadow: 0 4px 14px 0 rgb(0 0 0 / var(--preview-shadow));

  transition: transform 0.3s ease;

  ${CardNew} & {
    box-shadow: 0 4px 24px rgb(77 31 156 / var(--preview-shadow));
  }

  ${Card}:hover & {
    transform: translateY(10px);
  }

  ${Card}:hover & {
    --preview-shadow: 20%;
  }
`

const CoverWrap = styled.div<{ color?: string }>`
  position: relative;

  overflow: hidden;
  flex: 1;

  width: 260px;
  height: 180px;
  background: ${props => props.color || "#fff)"};

  border-radius: var(--border-radius-10);

  img {
    width: 100%;
    height: 100%;

    display: block;
    object-fit: cover;
  }
`

const Placeholder = styled(Horizontal)`
  position: absolute;
  z-index: 1;
  inset: 0;

  align-items: center;
  justify-content: center;

  background: var(--text-color-15);
  border-radius: var(--border-radius-10);

  &::after {
    color: var(--text-color-50);
    content: "260 × 180";
  }

  ${CardNew} & {
    background: var(--main-color-10);
  }
`

const Gradient = styled(Placeholder)`
  background: radial-gradient(96.26% 66.99% at 73.46% 92.23%, oklch(100% 0 89.876deg / 90%) 0%, oklch(95.4% 0.023 285.961deg / 90%) 100%), oklch(95.4% 0.023 285.961deg);

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    transition: transform 0.3s ease;

    background: conic-gradient(from 81deg at 50% 50%, oklch(90.2% 0.145 196.508deg / 50%) 13.8461deg, oklch(74.2% 0.137 282.535deg / 50%) 37.5221deg, oklch(77.9% 0.158 312.334deg / 50%) 157.1075deg, oklch(84.5% 0.123 70.58deg / 50%) 203.6588deg, oklch(78.8% 0.167 321.924deg / 50%) 217.7910deg, oklch(75.1% 0.141 292.906deg / 50%) 338.8353deg);
    filter: blur(40px);
    content: "";
  }

  ${CardNew}:hover &::after {
    transform: rotate(70.328deg);
  }
`

const TaskList = styled.div`
  position: relative;

  transform: translateY(-10px);
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: translateY(-30px);
  }
`

const TaskItem = styled.div`
  position: absolute;
  left: 50%;

  width: 146px;
  height: 92px;
  margin-left: -73px;
  padding: 4px 8px;
  border: 4px solid oklch(100% 0 89.876deg);
  border-radius: var(--border-radius-8);

  background: var(--background-color);
  box-shadow: 0 4px 14px 0 oklch(0% 0 0deg / 15%);

  transition: transform 0.3s ease;

  ${CardNew} & {
    color: oklch(39.5% 0.001 17.215deg / 20%);
  }

  ${textStyles.smallFontSize};
  line-height: 140%;

  &:nth-child(1) {
    z-index: -1;
    transform: rotate(-2.66deg) translateX(-30px) translateY(10px);
  }

  &:nth-child(2) {
    z-index: 0;
    transform: rotate(5.667deg);
  }

  &:nth-child(3) {
    z-index: -2;
    transform: rotate(4.759deg) translateX(30px) translateY(10px);
  }

  ${Card}:hover &:nth-child(1) {
    transform: rotate(-8.5deg) translateX(-30px) translateY(-8px);
  }

  ${Card}:hover &:nth-child(2) {
    transform: rotate(11.2deg) translateY(-15px);
  }

  ${Card}:hover &:nth-child(3) {
    transform: rotate(15deg) translateX(30px) translateY(-10px);
  }
`

const Underlay = styled.div<{ color?: string }>`
  position: absolute;
  inset: 0;

  opacity: 0;
  transition: opacity 0.3s ease;

  background: ${props => {
    if (!props.color || props.color === "#F3EAEA") {
      // Cards with cover images or **special** color use dark overlay
      return "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 45%, rgba(0, 0, 0, 0.1) 100%)"
    }

    // All other colors use colored overlay
    return `linear-gradient(180deg, ${props.color}00 45%, ${props.color}80 100%)`
  }};

  border-radius: var(--border-radius-12);
  pointer-events: none;

  ${Card}:hover & {
    opacity: 1;
  }

  ${CardNew}:hover & {
    opacity: 1;
  }
`

const InvisibleImage = css`
  visibility: hidden;
`

const StyledDropdownMenuItem = styled(DropdownMenuItem)<{ disabled?: boolean }>`
  opacity: ${props => (props.disabled ?? false ? 0.5 : 1)};
`

const SettingsLink = styled(Link)`
  color: inherit;

  &:hover {
    color: inherit;
  }
`
